import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { view } from '@yola/ws-sdk';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import analytics from 'src/js/modules/analytics';
import withSelection from 'src/js/modules/utils/with-selection';
import Trigger from 'src/js/modules/common/components/trigger';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import getBreakpointTypeTraitValue from 'src/js/modules/extensions/common/helpers/get-breakpoint-type-trait-value';
import restoreRange from '../helpers/restore-range';
import useFontSizeControl from '../helpers/use-font-size-control';
import changeFontSize from '../helpers/change-font-size';

const {
  segment: {
    trackers: { trackTextElementSizeAdjusted },
  },
} = analytics;

const { Slider, Icon } = designSystem;

const ICON_SIZE = 20;
const ICON_STROKE_WIDTH = 2;

const FontSizeSlider = ({ elementId, getContext }) => {
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;

  const { selection, focusElements, options, currentValue } = useFontSizeControl(
    elementId,
    activeBreakpoint
  );

  const min = 0;
  const max = options.length - 1;
  const [sliderValue, setSliderValue] = useState(min);
  const stateRef = useRef({});
  const latestRuntimeRef = useRef({
    options: [],
    focusElements: [],
  });
  const { controlPane } = getContext();

  latestRuntimeRef.current = {
    options,
    focusElements,
  };

  const handleSubmit = useCallback(async () => {
    const { current } = stateRef;
    const { options: latestOptions, focusElements: latestFocusElements } = latestRuntimeRef.current;
    const selectedOption = latestOptions[current.value] || current.options?.[current.value];
    const modifier = selectedOption?.value ?? current.lastModifier;
    const fontSize = selectedOption?.label ?? modifier;

    if (current.value === current.initialValue || !modifier) return;

    const breakpointType = isBreakpointEditingEnabled
      ? getBreakpointTypeTraitValue(activeBreakpoint)
      : null;

    changeFontSize(modifier, latestFocusElements, activeBreakpoint);
    await view.operations.regenerateAtomicCss();

    try {
      restoreRange(current.selection, elementId);
    } catch (error) {
      console.error(error);
    }

    trackTextElementSizeAdjusted({
      elementId,
      fontSize,
      breakpointType,
    });
  }, [elementId, isBreakpointEditingEnabled, activeBreakpoint]);

  useLayoutEffect(() => {
    if (stateRef.current.initialValue !== undefined) return;

    const val = options.findIndex((item) => item.value === currentValue);
    if (val === -1) return;

    setSliderValue(val);
    stateRef.current = {
      ...stateRef.current,
      selection,
      options,
      focusElements,
      value: val,
      initialValue: val,
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(
    () => () => {
      handleSubmit();
    },
    [handleSubmit]
  );

  const updateValue = async (val) => {
    setSliderValue(val);
    stateRef.current = {
      ...stateRef.current,
      value: val,
      options,
      lastModifier: options[val].value,
    };

    changeFontSize(options[val].value, focusElements, activeBreakpoint);
    await view.operations.regenerateAtomicCss();

    controlPane.forceHighlighterRerender();
  };

  const handleChange = (val) => {
    // on the first render handleChange is being called with wrong value
    // to fix this we need this check in order to not set wrong className
    if (sliderValue === -1) return;

    updateValue(val);
  };

  const handleValueControlClick = (type) => () => {
    const boundary = type === 'decrease' ? min : max;
    if (sliderValue === boundary) return;

    const num = type === 'decrease' ? -1 : 1;
    const val = sliderValue + num;
    updateValue(val);
  };

  const tooltipValueFormatter = (value) => {
    const { label = min } = options[value] || {};
    return label;
  };
  return (
    <div className="ws-font-size-slider">
      <div className="ws-font-size-slider__value-control">
        <Trigger onClick={handleValueControlClick('decrease')}>
          <Icon glyph="minus" size={ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />
        </Trigger>
      </div>
      <div className="ws-font-size-slider__control">
        <Slider
          tooltipValueFormatter={tooltipValueFormatter}
          isTooltipEnabled
          min={min}
          max={max}
          value={sliderValue}
          onChange={handleChange}
        />
      </div>
      <div className="ws-font-size-slider__value-control ws-font-size-slider__value-control--last">
        <Trigger onClick={handleValueControlClick('increase')}>
          <Icon glyph="plus-for-button" size={ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />
        </Trigger>
      </div>
    </div>
  );
};

FontSizeSlider.propTypes = {
  elementId: PropTypes.string.isRequired,
  getContext: PropTypes.func.isRequired,
};

export default withSelection(FontSizeSlider);
